import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { fetchIntegrations, type IntegrationStatus } from '@/lib/integrations.ts';

/** How often to ask again while the first calendar sync runs. */
const SYNC_POLL_MS = 4_000;

/**
 * The backstop on that. A sync that still calls itself pending after this long
 * will not finish while somebody watches, and a page that polls for ever keeps
 * a tab busy long after it was abandoned.
 */
const SYNC_POLL_LIMIT_MS = 3 * 60_000;

export interface FirstCalendarSync {
  /** Some connected calendar is still on its first read. */
  pending: boolean;
  /** A connected calendar's first read ended badly. */
  failed: boolean;
  /** Whether the polling budget is still open. */
  withinBudget: boolean;
}

/** Whether some connected calendar is still on its first read. */
function firstSyncRunningIn(integrations: IntegrationStatus[]): boolean {
  return integrations.some((entry) => entry.connected && entry.sync_status === 'pending');
}

function firstSyncFailedIn(integrations: IntegrationStatus[]): boolean {
  return integrations.some((entry) => entry.connected && entry.sync_status === 'error');
}

/**
 * How far the first read of a connected calendar has come, asked again while it
 * runs. Whether it is still running is the connection's own answer, never a
 * guess from an empty page. `fills` is what the sync fills — a watched finish
 * asks for it once more, so pass a key that keeps its identity across renders.
 */
export function useFirstCalendarSync(fills: QueryKey): FirstCalendarSync {
  /*
   * The budget as state rather than a clock read during render: a timer that
   * has fired is a fact the component can be re-rendered from, where "how long
   * ago did this page open" is a different answer every time it is asked.
   */
  const [withinBudget, setWithinBudget] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setWithinBudget(false), SYNC_POLL_LIMIT_MS);
    return () => clearTimeout(timer);
  }, []);

  const { data: integrations } = useQuery({
    queryKey: ['integrations', 'sync'],
    queryFn: fetchIntegrations,
    refetchInterval: (query) => {
      const running = query.state.data ? firstSyncRunningIn(query.state.data) : true;
      return running && withinBudget ? SYNC_POLL_MS : false;
    },
  });

  const queryClient = useQueryClient();

  /* Only a watched finish: one that was already done offers nothing newer. */
  const sawSyncRunning = useRef(false);

  useEffect(() => {
    if (integrations === undefined) return;
    if (firstSyncRunningIn(integrations)) {
      sawSyncRunning.current = true;
      return;
    }
    if (sawSyncRunning.current) {
      sawSyncRunning.current = false;
      void queryClient.invalidateQueries({ queryKey: fills });
    }
  }, [integrations, queryClient, fills]);

  /* Unanswered counts as running: "still reading" is the honest fallback. */
  return {
    pending: integrations ? firstSyncRunningIn(integrations) : true,
    failed: integrations ? firstSyncFailedIn(integrations) : false,
    withinBudget,
  };
}
